import PropTypes from 'prop-types';
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Helmet } from 'react-helmet';

import { PRODUCT_DISPLAY_NAMES } from '@/constants';
import { rebrandTheme } from 'theme/rebrand';

import ThemeErrorBoundary from './ThemeErrorBoundary';

/**
 * Safe DOM operation wrapper with error handling
 */
const safelyExecute = (operation, fallback = null) => {
  try {
    return operation();
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      console.error('ThemeProviderWrapper: Safe execution failed', error);
    }
    return fallback;
  }
};

const ThemeProviderWrapperInner = ({ children }) => {
  const [fontLoadError, setFontLoadError] = useState(false);

  // Load DM Sans font
  useEffect(() => {
    let link = null;
    let errorHandler = null;

    const loadFont = () => {
      try {
        // Check if font link already exists
        const existingLink = document.getElementById('rebrand-font');
        if (existingLink) {
          return;
        }

        link = document.createElement('link');
        link.href =
          'https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,100..1000;1,9..40,100..1000&display=swap';
        link.rel = 'stylesheet';
        link.id = 'rebrand-font';

        // Handle font loading errors gracefully
        errorHandler = () => {
          if (process.env.NODE_ENV !== 'production') {
            console.warn('Failed to load rebrand font, falling back to legacy');
          }
          setFontLoadError(true);
        };

        link.addEventListener('error', errorHandler);
        document.head.appendChild(link);
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error loading rebrand font:', error);
        }
        setFontLoadError(true);
      }
    };

    loadFont();

    return () => {
      safelyExecute(() => {
        const existingLink = document.getElementById('rebrand-font');
        if (existingLink) {
          if (errorHandler) {
            existingLink.removeEventListener('error', errorHandler);
          }
          document.head.removeChild(existingLink);
        }
      });
    };
  }, []);

  // Use useLayoutEffect to avoid FOUC (Flash of Unstyled Content)
  useLayoutEffect(() => {
    const applyTheme = () => {
      const root = document.documentElement;

      if (!root) {
        throw new Error('document.documentElement not available');
      }

      // Apply rebrand variables
      Object.entries(rebrandTheme).forEach(([property, value]) => {
        safelyExecute(() => root.style.setProperty(property, value));
      });

      // Add a class for specific overrides if needed
      safelyExecute(() => root.classList.add('rebrand-theme'));

      // Swap favicon
      safelyExecute(() => {
        const favicon = document.querySelector('link[rel="icon"]');
        if (favicon) {
          favicon.href = '/favicon-rebrand.svg';
        }
      });
    };

    // Wrap the entire theme application in error handling
    try {
      applyTheme();
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Failed to apply theme:', error);
      }
      // On error, ensure we fall back to legacy theme
      const root = document.documentElement;
      if (root) {
        safelyExecute(() => root.classList.remove('rebrand-theme'));
      }
    }
  }, []);

  // Log font load error for debugging
  useEffect(() => {
    if (fontLoadError && process.env.NODE_ENV !== 'production') {
      console.warn(
        'Rebrand font failed to load. Application will use fallback font.',
      );
    }
  }, [fontLoadError]);

  return (
    <>
      <Helmet>
        <title>{PRODUCT_DISPLAY_NAMES.TRIP_SCHEDULER}</title>
      </Helmet>
      {children}
    </>
  );
};

ThemeProviderWrapperInner.propTypes = {
  children: PropTypes.node.isRequired,
};

const ThemeProviderWrapper = ({ children }) => (
  <ThemeErrorBoundary>
    <ThemeProviderWrapperInner>{children}</ThemeProviderWrapperInner>
  </ThemeErrorBoundary>
);

ThemeProviderWrapper.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ThemeProviderWrapper;
